/**
 * The tutorial, mounted once for every authenticated page (TUTORIAL-STATE-001).
 *
 * Mounted in `AppShell`, which is the only place on this platform that wraps
 * every signed-in route and nothing else. That choice does most of the gating
 * for free: the login page, the kiosk, the driver tablet, the auditor and
 * care-home portals and the platform-admin area all render outside the shell,
 * so none of them can show a tenant user's tutorial to somebody standing at a
 * terminal.
 *
 * ── WHY A CONTEXT ──────────────────────────────────────────────────────────
 *
 * The tour needs to be startable from two places that are nowhere near each
 * other in the tree: the invitation (here) and the "Show me around" button in
 * the header, which each PAGE renders for itself. A context is what lets the
 * header reach the running tour without every page passing a prop down to it.
 *
 * ── THE ONE THING THIS DOES NOT DO ─────────────────────────────────────────
 *
 * It does not decide whether the person is new. The server does, and the answer
 * arrives as `shouldOffer` on the state. Putting that rule here would be the
 * third copy of it — the service has it, and any surface that mounted the
 * invitation would want it too.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchMe } from '../lib/meApi';
import {
  TUTORIAL_STATE_KEY,
  fetchTutorialState,
  recordTutorialEvent,
  type TutorialState,
} from '../lib/tutorialApi';
import { MAX_OFFERS } from './tutorialConstants';
import { TutorialInvite } from './TutorialInvite';
import { TutorialSpotlight } from './TutorialSpotlight';
import { useTutorial } from './useTutorial';

interface TutorialContextValue {
  /** Start from the beginning. */
  launch: () => void;
  /** Start from where they left off. Null when there is nowhere to resume to. */
  resume: (() => void) | null;
  running: boolean;
  /**
   * False while the state query is still in flight or has failed. The header
   * button hides rather than offering a tour we cannot record the result of.
   */
  ready: boolean;
}

const Ctx = createContext<TutorialContextValue | null>(null);

/**
 * For the "Show me around" control in the header.
 *
 * Returns null outside the provider — which is the case for every screen that
 * renders a `Header` without the AppShell around it (the portals, the kiosk
 * setup pages). The control renders nothing there, rather than throwing.
 */
export function useTutorialLaunch(): TutorialContextValue | null {
  return useContext(Ctx);
}

export function TutorialProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const controller = useTutorial();

  /*
   * Same key the Sidebar uses, so this is served from cache on every page
   * rather than being a second request for the same thing. Only the first name
   * is wanted, and only for the greeting.
   */
  const meQ = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => fetchMe(signal),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const stateQ = useQuery<TutorialState>({
    queryKey: TUTORIAL_STATE_KEY,
    queryFn: ({ signal }) => fetchTutorialState(signal),
    /*
     * Asked once per session, not per page. The state only changes through this
     * component's own mutations, which write the response straight into the
     * cache, so a refetch would be a request whose answer we already have.
     */
    staleTime: Infinity,
    retry: false,
  });

  const state = stateQ.data ?? null;

  const writeState = useCallback(
    (next: TutorialState): void => {
      queryClient.setQueryData(TUTORIAL_STATE_KEY, next);
    },
    [queryClient],
  );

  /*
   * ── WHY THE INVITATION LATCHES ─────────────────────────────────────────────
   *
   * The obvious condition for showing it is `state.shouldOffer && !running`,
   * straight from the server. It has a bug that only appears on somebody's
   * SECOND login, which is about the worst place for one:
   *
   * The invitation records `offered` as it renders. On the second offer that
   * takes `offeredCount` to MAX_OFFERS, the response comes back with
   * `shouldOffer: false` — and the dialog the user is in the middle of reading
   * disappears from under them. Their last chance to take the tour, closed by
   * the act of showing it to them.
   *
   * So `shouldOffer` decides whether to OPEN it, once, when the state first
   * arrives, and only an answer closes it. The counter still does its job: next
   * login `shouldOffer` is already false and it never opens.
   *
   * `null` is "not decided yet", which is not the same as false. While the
   * state query is in flight there is no answer, and latching false there would
   * mean a slow connection permanently suppressed the invitation for that
   * session.
   */
  const [inviteOpen, setInviteOpen] = useState<boolean | null>(null);

  /*
   * `offeredCount` AS IT WAS WHEN THE INVITATION OPENED, not as it is now.
   *
   * The note under the buttons reads this to decide between "we will offer once
   * more" and "this is the last time". Read live, it is always the second one:
   * the invitation records `offered` as it renders, the response comes back with
   * the count incremented, and the note re-renders against the new value. So a
   * first-time user was told we would not ask again, on the very first offer —
   * visible in the browser and invisible to the test that only checked the two
   * wordings exist.
   */
  const [openedAtCount, setOpenedAtCount] = useState<number | null>(null);

  useEffect(() => {
    if (state === null || inviteOpen !== null) return;
    setInviteOpen(state.shouldOffer);
    setOpenedAtCount(state.offeredCount);
  }, [inviteOpen, state]);

  const closeInvite = useCallback((): void => {
    setInviteOpen(false);
  }, []);

  /*
   * The invitation's two answers. Each closes it locally and then records — in
   * that order, because the record is a round trip and the dialog should go the
   * moment it is answered.
   *
   * There was a third, `onDecline`, behind a "No thanks" button. Removed
   * 2026-09-23 at Reagan's request; the counter already stops the invitation
   * after the second "Not now", so it bought nothing but a permanent decision
   * asked four seconds in. `declined` is still recorded — by skipping out of a
   * running tour, in useTutorial — so the status has not gone anywhere.
   */
  const onShown = useCallback((): void => {
    void recordTutorialEvent({ event: 'offered' }).then(writeState).catch(() => {
      /*
       * A failed write means the offer was not counted. Deliberately silent:
       * the cost is being asked once more, and an error toast about a tutorial
       * counter is worse than that.
       */
    });
  }, [writeState]);

  const onDefer = useCallback((): void => {
    closeInvite();
    void recordTutorialEvent({ event: 'deferred' }).then(writeState).catch(() => {});
  }, [closeInvite, writeState]);

  const onStart = useCallback((): void => {
    closeInvite();
    controller.start(null);
  }, [closeInvite, controller]);

  const value = useMemo<TutorialContextValue>(() => {
    const canResume =
      state !== null && state.lastStepKey !== null && state.status !== 'done';
    return {
      launch: () => controller.start(null),
      resume: canResume ? () => controller.start(state.lastStepKey) : null,
      running: controller.running,
      ready: state !== null,
    };
  }, [controller, state]);

  /*
   * The `running` clause is still needed on top of the latch, for the case
   * where the tour is started from the HEADER while the invitation happens to
   * be open — the latch has not been answered, and the invitation would sit on
   * top of the spotlight.
   */
  const showInvite = inviteOpen === true && !controller.running;

  return (
    <Ctx.Provider value={value}>
      {children}
      {showInvite && (
        <TutorialInvite
          firstName={meQ.data?.user.firstName ?? null}
          isFinalOffer={(openedAtCount ?? 0) >= MAX_OFFERS - 1}
          onShown={onShown}
          onStart={onStart}
          onDefer={onDefer}
        />
      )}
      <TutorialSpotlight controller={controller} />
    </Ctx.Provider>
  );
}
